export type FeatureExplanationBlock =
  | OperatorExplanationBlock
  | FeatureExplanationTextBlock
  | FeatureExplanationColumnBlock
  | FeatureExplanationPetBlock
  | FeatureExplanationDataSlotBlock
  | FeatureExplanationTopicBlock
  | FeatureExplanationTextWithDataSlotContextBlock;

export namespace FeatureExplanationBlock {
  export enum Type {
    PredictionExecutionTime = 'PetToken',
    DataSlot = 'DataSlotToken',
    Text = 'TextToken',
    Column = 'ColumnToken',
    TargetColumn = 'TargetColumnToken',
    Topic = 'TopicToken',
    TextWithDataSlotContext = 'textWithDataSlotContext',

    Map = 'MapToken',
    Reduce = 'ReduceToken',
    Filter = 'FilterToken',
    TimeDiff = 'TimeDiffToken',
    PeriodicTimeDiff = 'PeriodicTimeDiffToken',
    DistanceMap = 'DistanceMapToken',
  }

  export const isOperatorExplanationBlock = (
    block: FeatureExplanationBlock
  ): block is OperatorExplanationBlock => 'tokens' in block;

  export const isTextExplanationBlock = (
    block: FeatureExplanationBlock
  ): block is FeatureExplanationTextBlock => Type.Text === block.type;

  export const isColumnExplanationBlock = (
    block: FeatureExplanationBlock
  ): block is FeatureExplanationColumnBlock =>
    [Type.Column, Type.TargetColumn].includes(block.type);

  export const isDataSlotExplanationBlock = (
    block: FeatureExplanationBlock
  ): block is FeatureExplanationDataSlotBlock => Type.DataSlot === block.type;

  export const isPetExplanationBlock = (
    block: FeatureExplanationBlock
  ): block is FeatureExplanationPetBlock =>
    Type.PredictionExecutionTime === block.type;

  export const isTopicExplanationBlock = (
    block: FeatureExplanationBlock
  ): block is FeatureExplanationTopicBlock => Type.Topic === block.type;

  export const isTextWithDataSlotContextExplanationBlock = (
    block: FeatureExplanationBlock
  ): block is FeatureExplanationTextWithDataSlotContextBlock =>
    Type.TextWithDataSlotContext === block.type;
}

export interface OperatorExplanationBlock {
  type:
    | FeatureExplanationBlock.Type.Map
    | FeatureExplanationBlock.Type.Reduce
    | FeatureExplanationBlock.Type.Filter
    | FeatureExplanationBlock.Type.TimeDiff
    | FeatureExplanationBlock.Type.PeriodicTimeDiff
    | FeatureExplanationBlock.Type.DistanceMap;
  tokens: FeatureExplanationBlock[];
}

export interface FeatureExplanationTextBlock {
  type: FeatureExplanationBlock.Type.Text;
  text: string;
}

export interface FeatureExplanationColumnBlock {
  type:
    | FeatureExplanationBlock.Type.Column
    | FeatureExplanationBlock.Type.TargetColumn;
  columnName: string;
  columnNameAlias?: string;
  dataSlotIndex: number; // -1 for target schema, 0 for first source schema, 1 for second, etc.
  analyticSchemaName?: string;
  analyticSchemaId?: string;
  dataSlotName?: string;
  resolutionUnit?: string;
  dataLeadTime?: any;
  pathType: string;
}

export interface FeatureExplanationDataSlotBlock {
  type: FeatureExplanationBlock.Type.DataSlot;
  dataSlotIndex: number;
  dataSlotName?: string;
  analyticSchemaName: string;
}

export interface FeatureExplanationPetBlock {
  type: FeatureExplanationBlock.Type.PredictionExecutionTime;
  stringRepresentation: string;
  pathType: string;
}

export interface FeatureExplanationTopicBlock {
  type: FeatureExplanationBlock.Type.Topic;
  id: string;
  name: string;
  dataSlotIndex: number;
}

export interface FeatureExplanationTextWithDataSlotContextBlock {
  type: FeatureExplanationBlock.Type.TextWithDataSlotContext;
  dataSlotIndex: number;
  text: string;
  pathType: string;
}
